import { CommonModule, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AttachmentPreviewComponent } from '../../../shared/attachment-preview/attachment-preview.component';

import { ShipmentDeliveryAttachment } from '../../models/shipment-delivery.attachment';
import { ShipmentDelivery } from '../../models/delivery/shipment-delivery.model';
import { ShipmentDeliveryOperationStatus } from '../../models/workflow/shipment-workflow.model';
import { formatShipmentDispatchStatus } from '../dispatch/shipment-dispatch.presenter';

export type ShipmentFilesCommand =
    | { type: 'add-file' }
    | { type: 'delete-file'; file: ShipmentDeliveryAttachment };

/** Attachment presentation only. File uploads and deletion remain coordinated by the route shell. */
@Component({
    selector: 'app-shipment-files-panel',
    standalone: true,
    templateUrl: './shipment-files-panel.component.html',
    styleUrls: ['./shipment-files-panel.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ButtonModule, TooltipModule, AttachmentPreviewComponent]
})
export class ShipmentFilesPanelComponent {
    readonly files = input<ShipmentDeliveryAttachment[]>([]);
    readonly deliveryById = input<Map<number, ShipmentDelivery>>(new Map());
    readonly jobById = input<Map<number, ShipmentDelivery>>(new Map());
    readonly shipmentDeliveries = input<ShipmentDelivery[]>([]);
    readonly operationStatuses = input<Record<number, ShipmentDeliveryOperationStatus>>({});
    readonly canManageFiles = input(false);
    readonly command = output<ShipmentFilesCommand>();

    attachmentDelivery(file: ShipmentDeliveryAttachment): ShipmentDelivery | undefined {
        const deliveryId = Number(file?.deliveryId);
        if (!Number.isFinite(deliveryId) || deliveryId <= 0) return undefined;
        return this.deliveryById().get(deliveryId)
            ?? this.jobById().get(deliveryId)
            ?? this.shipmentDeliveries().find(delivery => Number(delivery.deliveryId) === deliveryId);
    }

    scopeLabel(file: ShipmentDeliveryAttachment): string {
        const delivery = this.attachmentDelivery(file);
        return delivery ? `Delivery ${delivery.deliveryNumber || file.deliveryNumber || '#' + file.deliveryId}` : 'Shipment level';
    }

    scopeSubtitle(file: ShipmentDeliveryAttachment): string {
        const delivery = this.attachmentDelivery(file);
        if (!delivery) return 'Applies to the shipment';
        const stopLocationName = this.stopLocation(file, delivery);
        if (stopLocationName) return stopLocationName;
        const deliveryType = delivery.deliveryType ? formatShipmentDispatchStatus(String(delivery.deliveryType)) : 'Delivery';
        return `${deliveryType} attachment`;
    }

    scopeIcon(file: ShipmentDeliveryAttachment): string {
        if (!this.attachmentDelivery(file)) return 'fa-solid fa-paperclip';
        return file.stopId ? 'fa-solid fa-location-dot' : 'fa-solid fa-truck-moving';
    }

    isShipmentLevel(file: ShipmentDeliveryAttachment): boolean {
        return !this.attachmentDelivery(file);
    }

    formattedCreationDate(value: number | undefined): string {
        return value ? formatDate(new Date(value), 'dd-MM-yyyy HH:mm', 'en-US') : '';
    }

    private stopLocation(file: ShipmentDeliveryAttachment, delivery: ShipmentDelivery): string | undefined {
        const stopId = Number(file?.stopId);
        if (!Number.isFinite(stopId) || stopId <= 0) return undefined;
        const operationStatus = this.operationStatuses()[delivery.deliveryId];
        const stops = [operationStatus?.activeStop, operationStatus?.nextStop, ...(operationStatus?.stops ?? [])].filter(Boolean);
        return stops.find(stop => Number(stop?.stopId) === stopId)?.locationName?.trim() || undefined;
    }
}
